import { useAction, useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { LightningIcon, Numeric } from '@blade/ui-kit'
import { openBuyBoostersFlowEvent } from '@root/entities/premium'
import { ShoppingCartIcon } from '@root/shared/ui-kit'

import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium/atoms'

type Mode = 'trade' | 'explore'

type Props = {
  currency: CurrencyModel
  premiumInfo: PremiumMetaModel
  /**
   * Allow open boost dialog from the boosters
   */
  withBoost?: boolean
  mode?: Mode
}

export const PairBoosters = ({
  currency,
  premiumInfo,
  mode = 'trade',
  withBoost = false,
}: Props) => {
  const { t } = useTranslation()

  const [boosters] = useAtom(premiumInfo.boosters)

  const openBuyBoosters = useAction(openBuyBoostersFlowEvent)

  const handleBuyBoosters = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    openBuyBoosters({ currency, premium: premiumInfo })
  }

  if (!boosters && !withBoost) {
    return undefined
  }

  return (
    <Root>
      <Boost
        data-mode={mode}
        data-variant={(() => {
          if (boosters > 0) {
            if (withBoost) {
              return 'boosters-and-boost'
            }
            return 'boosters-only'
          }
          if (withBoost) {
            return 'boost-only'
          }
        })()}
        onClick={handleBuyBoosters}
      >
        <LightningIcon size={9} />
        {boosters ? (
          <Count>
            <Numeric value={boosters} preset="splash-points" />
            {withBoost ? (
              <BoostIcon>
                <ShoppingCartIcon size={9} color="var(--boostGradientTo-9)" />
              </BoostIcon>
            ) : undefined}
          </Count>
        ) : (
          t('premium.button.boost')
        )}
      </Boost>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  border: 1px solid transparent;
  border-radius: 20px;
  background:
    linear-gradient(var(--background-3), var(--background-3)) padding-box,
    linear-gradient(
        135deg,
        var(--boostGradientFrom-9) 0%,
        var(--boostGradientTo-9) 100%
      )
      border-box;

  &:hover {
    background:
      linear-gradient(var(--background-3), var(--background-3)) padding-box,
      linear-gradient(
          150deg,
          var(--boostGradientFrom-9) 0%,
          var(--boostGradientTo-9) 100%
        )
        border-box;
  }
`

const Boost = styled.button`
  pointer-events: auto;
  background: var(--background-3);
  color: var(--boostButton-9);
  border: none;
  outline: none;
  cursor: pointer;
  display: grid;
  align-items: center;
  border-radius: 40px;
  grid-template-columns: auto auto;
  height: 16px;

  &[data-mode='explore'] {
    font-size: 9px;
    font-weight: 700;
    padding-inline: 6px;
  }

  &[data-mode='trade'] {
    font-size: 10px;
    font-weight: 800;
    padding-inline: 8px;
  }

  &[data-variant='boosters-and-boost'] {
    padding-inline-end: 1px;
  }

  &[data-variant='boosters-only'] {
    padding-inline-end: 6px;
  }

  &[data-variant='boost-only'] {
    padding-inline-end: 6px;
  }
`

const Count = styled.div`
  display: grid;
  grid-auto-flow: column;

  align-items: center;
  height: 100%;
  gap: 2px;
`

const BoostIcon = styled.span`
  background: var(--appearance-1);

  display: grid;
  place-content: center;

  border-radius: 50px;
  width: 20px;
  height: 100%;
`
